import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import MapService, { type MapChanges, type MapRecord } from '../../../../services/back4app/MapService'
import { fileService } from '../../../../services/back4app'
import { virtualRoomService, type VirtualRoomData } from '../../../../services/back4app/VirtualRoomService'
import { clearMapEditorReturn, joinMapEditorRoom, peekMapEditorReturn } from '../../../../services/mapEditorRoom'
import { getServerBaseUrl } from '../../../../utils/serverUrl'
import { getStoredSessionToken } from '../../../../utils/sessionToken'
import type { TilePoint } from '../../../../../../types/Maps'

/**
 * Platform admin Maps section under Admin (SC-422 plan Task 9.1). Maps come from `tilesets.json`; registering
 * one creates its `Map` row. Settings and the reception go through Cloud Code, which enforces the
 * map rules and returns rule errors as they are. Running rooms show the VR revision they loaded,
 * so outdated ones can be restarted (plan D1), and "Edit virtual rooms" joins the map's editor room.
 */

export interface MapDefinition {
  id: string
  name: string
  path: string
  tileWidth?: number
  tileHeight?: number
  /** In pixels. */
  playerStart?: { x: number; y: number }
}

export interface RunningRoom {
  roomId: string
  customRoomId: string | null
  companyId: string | null
  name: string
  mapId: string | null
  clients: number
  isLobby: boolean
  isMapEditorRoom?: boolean
  vrRevision: number | null
}

export interface MapListEntry {
  mapId: string
  name: string
  definition: MapDefinition | null
  record: MapRecord | null
}

export interface MapForm {
  name: string
  description: string
  thumbnailUrl: string
  enabled: boolean
  isLobbyMap: boolean
  autoSeatOnJoin: boolean
  startX: string
  startY: string
}

const TILE_SIZE = 32
// SC-477: was 10 s, about five Back4App requests per tick. Two minutes still catches another
// admin's edit well before anyone restarts rooms from a stale list.
export const LIVE_REFRESH_MS = 120_000
/** Matches `MAX_DESCRIPTION_LENGTH` in `cloud/mapManager.js`. */
export const MAX_MAP_DESCRIPTION_LENGTH = 200

/** `tilesets.json` maps first, in file order, then any registered map missing from the file. */
export function mergeMapList(definitions: MapDefinition[], records: MapRecord[]): MapListEntry[] {
  const byId = new Map(records.map((r) => [r.mapId, r]))
  const entries: MapListEntry[] = definitions.map((d) => ({
    mapId: d.id,
    name: byId.get(d.id)?.name || d.name || d.id,
    definition: d,
    record: byId.get(d.id) ?? null,
  }))
  for (const record of records) {
    if (!definitions.some((d) => d.id === record.mapId)) {
      entries.push({ mapId: record.mapId, name: record.name, definition: null, record })
    }
  }
  return entries
}

/** `tilesets.json` gives the start in pixels; `Map.playerStart` is in tiles. */
export function playerStartInTiles(definition: MapDefinition): TilePoint {
  const px = definition.playerStart ?? { x: 0, y: 0 }
  return {
    x: Math.floor(px.x / (definition.tileWidth || TILE_SIZE)),
    y: Math.floor(px.y / (definition.tileHeight || TILE_SIZE)),
  }
}

/** Running rooms that loaded an older VR revision than the map has now. */
export function outdatedRooms(rooms: RunningRoom[], vrRevision: number): RunningRoom[] {
  return rooms.filter((room) => room.vrRevision === null || room.vrRevision < vrRevision)
}

export function formFromRecord(record: MapRecord): MapForm {
  return {
    name: record.name,
    description: record.description ?? '',
    thumbnailUrl: record.thumbnailUrl ?? '',
    enabled: record.enabled,
    isLobbyMap: record.isLobbyMap,
    autoSeatOnJoin: record.autoSeatOnJoin,
    startX: String(record.playerStart?.x ?? ''),
    startY: String(record.playerStart?.y ?? ''),
  }
}

/** Only the fields the admin changed, so a save never rewrites settings nobody touched. */
export function mapChanges(record: MapRecord, form: MapForm): MapChanges {
  const changes: MapChanges = {}
  if (form.name.trim() !== record.name) changes.name = form.name.trim()
  if (form.description.trim() !== (record.description ?? '')) changes.description = form.description.trim()
  if (form.thumbnailUrl !== (record.thumbnailUrl ?? '')) changes.thumbnailUrl = form.thumbnailUrl
  if (form.enabled !== record.enabled) changes.enabled = form.enabled
  if (form.isLobbyMap !== record.isLobbyMap) changes.isLobbyMap = form.isLobbyMap
  if (form.autoSeatOnJoin !== record.autoSeatOnJoin) changes.autoSeatOnJoin = form.autoSeatOnJoin
  const start = { x: Number(form.startX), y: Number(form.startY) }
  if (start.x !== record.playerStart?.x || start.y !== record.playerStart?.y) changes.playerStart = start
  return changes
}

export function restartConfirmMessage(rooms: RunningRoom[]): string {
  const players = rooms.reduce((sum, room) => sum + (room.clients || 0), 0)
  const roomWord = rooms.length === 1 ? 'room' : 'rooms'
  const playerWord = players === 1 ? 'player' : 'players'
  return `Restarting ${rooms.length} ${roomWord} disconnects ${players} ${playerWord}. Consider an off-peak time. Continue?`
}

const errorText = (error: unknown) => (error instanceof Error ? error.message : String(error))

function authHeaders() {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${getStoredSessionToken()}` }
}

async function fetchRunningRooms(mapId: string): Promise<RunningRoom[]> {
  const response = await fetch(`${getServerBaseUrl()}/api/room-control/rooms?mapId=${encodeURIComponent(mapId)}`, {
    headers: authHeaders(),
  })
  const body = await response.json().catch(() => ({}))
  if (!response.ok || !body.success) throw new Error(body.message || `Could not load running rooms (${response.status})`)
  return body.rooms || []
}

async function restartRoom(customRoomId: string): Promise<void> {
  const response = await fetch(`${getServerBaseUrl()}/api/room-control`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ action: 'restart', customRoomId }),
  })
  const body = await response.json().catch(() => ({}))
  if (!response.ok || body.success === false) throw new Error(body.message || `Restart failed (${response.status})`)
}

async function loadMapDefinitions(): Promise<MapDefinition[]> {
  const response = await fetch('/assets/tilesets/config/tilesets.json')
  const config = await response.json()
  return config.maps || []
}

/** The map's size in tiles, from its Tiled JSON. */
async function loadMapSize(definition: MapDefinition): Promise<{ width: number; height: number }> {
  const response = await fetch(`/assets/${definition.path}`)
  const json = await response.json()
  if (!Number.isInteger(json.width) || !Number.isInteger(json.height)) {
    throw new Error(`${definition.path} has no width and height`)
  }
  return { width: json.width, height: json.height }
}

interface AdminMapsProps {
  /** Back to the Admin menu. */
  onBack: () => void
}

export const AdminMaps: React.FC<AdminMapsProps> = ({ onBack }) => {
  // Read once on mount, so every list load selects the same map: the editor room the admin just
  // left (mapEditorRoom.ts). Cleared once read, so the next visit starts on the first map.
  const [initialMapId] = useState(peekMapEditorReturn)
  useEffect(() => {
    if (initialMapId) clearMapEditorReturn()
  }, [initialMapId])

  const [entries, setEntries] = useState<MapListEntry[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [form, setForm] = useState<MapForm | null>(null)
  const [vrs, setVrs] = useState<VirtualRoomData[]>([])
  const [running, setRunning] = useState<RunningRoom[]>([])
  const [loadingRunning, setLoadingRunning] = useState(false)
  const [confirmRooms, setConfirmRooms] = useState<RunningRoom[] | null>(null)

  const selected = useMemo(() => entries.find((e) => e.mapId === selectedId) ?? null, [entries, selectedId])

  const refreshList = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [definitions, records] = await Promise.all([loadMapDefinitions(), MapService.listAll()])
      const merged = mergeMapList(definitions, records)
      setEntries(merged)
      const initial = merged.some((e) => e.mapId === initialMapId) ? initialMapId : merged[0]?.mapId
      setSelectedId((current) => current ?? initial ?? null)
    } catch (e) {
      setError(errorText(e))
    } finally {
      setLoading(false)
    }
  }, [initialMapId])

  useEffect(() => {
    refreshList()
  }, [refreshList])

  const record = selected?.record ?? null
  const recordMapId = record?.mapId ?? null
  const shownRevision = useRef<number | null>(null)
  shownRevision.current = record?.vrRevision ?? null

  // Keyed on the map, not the record, so a live revision update neither drops unsaved form edits
  // nor brings back the running-rooms loader. Save resets the form itself.
  useEffect(() => {
    setForm(record ? formFromRecord(record) : null)
    setVrs([])
    setRunning([])
    setLoadingRunning(!!recordMapId)
    if (!recordMapId) return
    let cancelled = false
    const fail = (e: unknown) => !cancelled && setError(errorText(e))
    virtualRoomService
      .getRoomsByMapId(recordMapId)
      .then((mapVrs) => !cancelled && setVrs(mapVrs))
      .catch(fail)
    fetchRunningRooms(recordMapId)
      .then((rooms) => !cancelled && setRunning(rooms))
      .catch(fail)
      .finally(() => !cancelled && setLoadingRunning(false))
    return () => {
      cancelled = true
    }
  }, [recordMapId])

  // VR and reception edits made in the map editor or by another admin bump the map's revision, and
  // rooms start, restart and finish loading on their own. Polling while the page is visible keeps
  // the Outdated marks and "Restart all outdated" current without a reload.
  useEffect(() => {
    if (!recordMapId) return
    let cancelled = false
    let inFlight = false
    let lastRefreshAt = Date.now()
    const refresh = async () => {
      if (inFlight || document.hidden) return
      inFlight = true
      lastRefreshAt = Date.now()
      try {
        const [records, rooms] = await Promise.all([MapService.listAll(), fetchRunningRooms(recordMapId)])
        if (cancelled) return
        setRunning(rooms)
        const fresh = records.find((m) => m.mapId === recordMapId)
        if (!fresh || fresh.vrRevision === shownRevision.current) return
        // Only the VR side of the record: the settings stay as the form loaded them, so Save still
        // sends just what this admin changed.
        const live = {
          vrRevision: fresh.vrRevision,
          hasReception: fresh.hasReception,
          receptionVirtualRoomId: fresh.receptionVirtualRoomId,
        }
        setEntries((list) =>
          list.map((e) => (e.mapId === recordMapId && e.record ? { ...e, record: { ...e.record, ...live } } : e))
        )
        const mapVrs = await virtualRoomService.getRoomsByMapId(recordMapId)
        if (!cancelled) setVrs(mapVrs)
      } catch {
        // A failed refresh keeps what is shown; the next one retries.
      } finally {
        inFlight = false
      }
    }
    // Coming back to the tab catches up at once, but only after a full interval away: switching
    // tabs back and forth must not turn into a refresh per switch.
    const refreshOnTabReturn = () => {
      if (Date.now() - lastRefreshAt < LIVE_REFRESH_MS) return
      void refresh()
    }
    const timer = window.setInterval(refresh, LIVE_REFRESH_MS)
    document.addEventListener('visibilitychange', refreshOnTabReturn)
    return () => {
      cancelled = true
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', refreshOnTabReturn)
    }
  }, [recordMapId])

  const replaceRecord = (next: MapRecord) =>
    setEntries((list) => list.map((e) => (e.mapId === next.mapId ? { ...e, name: next.name, record: next } : e)))

  const run = async (action: () => Promise<void>, success?: string) => {
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      await action()
      if (success) setNotice(success)
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }

  const handleRegister = () =>
    run(async () => {
      const definition = selected!.definition!
      const size = await loadMapSize(definition)
      const created = await MapService.register({
        mapId: definition.id,
        name: definition.name || definition.id,
        playerStart: playerStartInTiles(definition),
        width: size.width,
        height: size.height,
      })
      replaceRecord(created)
    }, 'Map registered. It stays disabled until you enable it.')

  const handleSave = () =>
    run(async () => {
      const changes = mapChanges(record!, form!)
      if (Object.keys(changes).length === 0) return
      const updated = await MapService.update(record!.mapId, changes)
      replaceRecord(updated)
      setForm(formFromRecord(updated))
    }, 'Saved. Start point and auto-seat apply to rooms started after now.')

  // SC-438: the image uploads straight away; Save stores its URL on the map.
  const handleThumbnailUpload = (file: File) =>
    run(async () => {
      const extension = file.name.split('.').pop() || 'png'
      const { data, error: uploadError } = await fileService.uploadFile(
        file,
        `map-${record!.mapId}-${Date.now()}.${extension}`
      )
      if (uploadError || !data) throw new Error(uploadError?.message || 'The image could not be uploaded.')
      setForm((current) => (current ? { ...current, thumbnailUrl: data.publicUrl } : current))
    }, 'Image uploaded. Save to use it on the map card.')

  const handleReception = (virtualRoomId: string | null) =>
    run(async () => {
      const result = await MapService.setReception(record!.mapId, virtualRoomId)
      // A reception change bumps the map's VR revision, so reload the row: the running rooms are
      // compared with the new revision and can now be outdated.
      const fresh = (await MapService.listAll()).find((m) => m.mapId === record!.mapId)
      replaceRecord(
        fresh ?? {
          ...record!,
          receptionVirtualRoomId: result.receptionVirtualRoomId,
          hasReception: !!result.receptionVirtualRoomId,
        }
      )
    }, 'Reception updated.')

  const handleConfirmRestart = () => {
    const rooms = confirmRooms || []
    setConfirmRooms(null)
    run(async () => {
      for (const room of rooms) {
        if (room.customRoomId) await restartRoom(room.customRoomId)
      }
      setRunning(await fetchRunningRooms(record!.mapId))
    }, `Restarted ${rooms.length} room(s).`)
  }

  const handleEditVrs = () =>
    run(async () => {
      await joinMapEditorRoom(record!.mapId)
    })

  const outdated = record ? outdatedRooms(running, record.vrRevision) : []
  const outdatedIds = new Set(outdated.map((r) => r.roomId))

  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, py: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={onBack} aria-label="Back to admin menu" color="primary">
          <ArrowBackIcon />
        </IconButton>
        <Typography component="h1" variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
          Maps
        </Typography>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)} data-testid="maps-error">
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          <List dense sx={{ minWidth: 220, borderRight: { md: 1 }, borderColor: 'divider' }}>
            {entries.map((entry) => (
              <ListItemButton
                key={entry.mapId}
                selected={entry.mapId === selectedId}
                onClick={() => setSelectedId(entry.mapId)}
                data-testid={`map-${entry.mapId}`}
              >
                <ListItemText
                  primary={entry.name}
                  secondary={!entry.record ? 'Not registered' : entry.record.enabled ? 'Enabled' : 'Disabled'}
                />
              </ListItemButton>
            ))}
          </List>

          {selected && (
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" gutterBottom>
                {selected.name} <Typography component="span" color="text.secondary">({selected.mapId})</Typography>
              </Typography>

              {!record && (
                <Stack spacing={2} alignItems="flex-start">
                  <Typography color="text.secondary">
                    This map is in tilesets.json but has no Map row yet. Registering reads its size from the map
                    file and its start point from tilesets.json.
                  </Typography>
                  <Button variant="contained" onClick={handleRegister} disabled={busy || !selected.definition}>
                    Register
                  </Button>
                </Stack>
              )}

              {record && form && (
                <Stack spacing={3}>
                  <Stack spacing={2}>
                    <TextField
                      label="Name"
                      size="small"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                    />
                    {/* SC-438: shown on the map card when a company picks its office. */}
                    <TextField
                      label="Description"
                      size="small"
                      multiline
                      minRows={2}
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      inputProps={{ maxLength: MAX_MAP_DESCRIPTION_LENGTH }}
                      helperText="Who the layout suits, in a sentence or two"
                    />
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                      {form.thumbnailUrl ? (
                        <Box
                          component="img"
                          src={form.thumbnailUrl}
                          alt={`${form.name} preview`}
                          data-testid="maps-thumbnail-preview"
                          sx={{ width: 160, height: 96, objectFit: 'cover', borderRadius: 1, border: 1, borderColor: 'divider' }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No preview image
                        </Typography>
                      )}
                      <Button component="label" variant="outlined" size="small" disabled={busy}>
                        {form.thumbnailUrl ? 'Change image' : 'Upload image'}
                        <input
                          hidden
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          data-testid="maps-thumbnail-input"
                          onChange={(e) => {
                            const file = e.target.files?.[0]
                            e.target.value = ''
                            if (file) void handleThumbnailUpload(file)
                          }}
                        />
                      </Button>
                      {form.thumbnailUrl && (
                        <Button size="small" disabled={busy} onClick={() => setForm({ ...form, thumbnailUrl: '' })}>
                          Remove image
                        </Button>
                      )}
                    </Stack>
                    <Stack direction="row" spacing={2}>
                      <TextField
                        label="Start X (tile)"
                        size="small"
                        type="number"
                        value={form.startX}
                        onChange={(e) => setForm({ ...form, startX: e.target.value })}
                      />
                      <TextField
                        label="Start Y (tile)"
                        size="small"
                        type="number"
                        value={form.startY}
                        onChange={(e) => setForm({ ...form, startY: e.target.value })}
                      />
                    </Stack>
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                      <FormControlLabel
                        control={<Checkbox checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />}
                        label="Enabled"
                      />
                      <FormControlLabel
                        control={<Checkbox checked={form.isLobbyMap} onChange={(e) => setForm({ ...form, isLobbyMap: e.target.checked })} />}
                        label="Lobby map"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox checked={form.autoSeatOnJoin} onChange={(e) => setForm({ ...form, autoSeatOnJoin: e.target.checked })} />
                        }
                        label="Auto-seat on join"
                      />
                    </Stack>
                    <Box>
                      <Button variant="contained" onClick={handleSave} disabled={busy}>
                        Save
                      </Button>
                    </Box>
                  </Stack>

                  <FormControl size="small" sx={{ maxWidth: 360 }}>
                    {/* Raised label and "None" shown, so an unset reception doesn't read as the label. */}
                    <InputLabel id="maps-reception-label" shrink>
                      Reception
                    </InputLabel>
                    <Select
                      labelId="maps-reception-label"
                      label="Reception"
                      notched
                      displayEmpty
                      renderValue={(value) =>
                        value ? vrs.find((vr) => vr.roomId === value)?.name || value : 'None'
                      }
                      value={record.receptionVirtualRoomId ?? ''}
                      onChange={(e) => handleReception((e.target.value as string) || null)}
                      disabled={busy}
                      data-testid="maps-reception"
                    >
                      <MenuItem value="">None</MenuItem>
                      {vrs.map((vr) => (
                        <MenuItem key={vr.roomId} value={vr.roomId}>
                          {vr.name || vr.roomId}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      Select which virtual room is this map's reception. External guests arrive there, and a
                      lobby map needs one.
                    </FormHelperText>
                  </FormControl>

                  <Box>
                    <Button variant="outlined" onClick={handleEditVrs} disabled={busy}>
                      Edit virtual rooms
                    </Button>
                  </Box>

                  <Box>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                      <Typography variant="subtitle1">Running rooms</Typography>
                      <Chip size="small" label={`VR revision ${record.vrRevision}`} />
                      <Box sx={{ flex: 1 }} />
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        disabled={busy || outdated.length === 0}
                        onClick={() => setConfirmRooms(outdated)}
                      >
                        Restart all outdated
                      </Button>
                    </Stack>
                    {loadingRunning ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : running.length === 0 ? (
                      <Typography color="text.secondary">No rooms are running on this map.</Typography>
                    ) : (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Room</TableCell>
                            <TableCell>Players</TableCell>
                            <TableCell>VRs</TableCell>
                            <TableCell />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {running.map((room) => (
                            <TableRow key={room.roomId} data-testid={`running-${room.roomId}`}>
                              <TableCell>
                                {room.name}
                                {room.isLobby && <Chip size="small" label="Lobby" sx={{ ml: 1 }} />}
                                {room.isMapEditorRoom && <Chip size="small" label="Editor" sx={{ ml: 1 }} />}
                              </TableCell>
                              <TableCell>{room.clients}</TableCell>
                              <TableCell>
                                {outdatedIds.has(room.roomId) ? (
                                  <Chip size="small" color="warning" label="Outdated" />
                                ) : (
                                  <Chip size="small" color="success" label="Current" />
                                )}
                              </TableCell>
                              <TableCell align="right">
                                <Button size="small" disabled={busy || !room.customRoomId} onClick={() => setConfirmRooms([room])}>
                                  Restart
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </Box>
                </Stack>
              )}
            </Box>
          )}
        </Box>
      )}

      <Dialog open={!!confirmRooms} onClose={() => setConfirmRooms(null)}>
        <DialogTitle>Restart rooms</DialogTitle>
        <DialogContent>
          <Typography data-testid="restart-confirm">{restartConfirmMessage(confirmRooms || [])}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRooms(null)}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={handleConfirmRestart}>
            Restart
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default AdminMaps
